import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UserPlus, Trash2, Loader2, KeyRound, Ban, CheckCircle } from 'lucide-react';

export default function AdminManage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialog, setResetDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [form, setForm] = useState({ username: '', password: '', name: '', role: 'surveyor', team_leader_id: '' });
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    try {
      const { data } = await API.get('/admin/users');
      setUsers(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.post('/admin/users', {
        ...form,
        team_leader_id: form.team_leader_id ? parseInt(form.team_leader_id) : null,
      });
      setDialogOpen(false);
      setForm({ username: '', password: '', name: '', role: 'surveyor', team_leader_id: '' });
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating user');
    }
    setSaving(false);
  };

  const handleDeactivate = async (id) => {
    try {
      await API.post(`/admin/users/${id}/deactivate`);
      loadUsers();
    } catch {}
  };

  const handleResetPassword = async () => {
    if (!resetDialog || !newPassword) return;
    try {
      await API.post(`/admin/users/${resetDialog}/reset-password`, { new_password: newPassword });
      setResetDialog(null);
      setNewPassword('');
      alert('Password reset successfully');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try {
      await API.delete(`/admin/users/${id}`);
      loadUsers();
    } catch {}
  };

  const teamLeaders = users.filter(u => u.role === 'team_leader');
  const roleBadge = (role) => {
    const colors = { admin: 'bg-slate-900 text-white', team_leader: 'bg-blue-100 text-blue-800', surveyor: 'bg-emerald-100 text-emerald-800' };
    return <Badge className={`${colors[role] || 'bg-slate-100'} text-xs`}>{role.replace('_', ' ')}</Badge>;
  };

  if (loading) return <div className="flex justify-center pt-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-manage-users">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Manage Users</h1>
          <p className="text-slate-500 text-sm mt-1">{users.length} users registered</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="bg-slate-900 hover:bg-slate-800">
              <UserPlus className="w-4 h-4 mr-2" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Create User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div><Label>Name</Label><Input data-testid="user-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required /></div>
              <div><Label>Username</Label><Input data-testid="user-username-input" value={form.username} onChange={e => setForm({...form, username: e.target.value})} required placeholder="e.g. surveyor5" /></div>
              <div><Label>Password</Label><Input data-testid="user-password-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="team_leader">Team Leader</SelectItem>
                    <SelectItem value="surveyor">Surveyor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === 'surveyor' && (
                <div>
                  <Label>Team Leader</Label>
                  <Select value={form.team_leader_id} onValueChange={v => setForm({...form, team_leader_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select team leader" /></SelectTrigger>
                    <SelectContent>
                      {teamLeaders.map(tl => <SelectItem key={tl.id} value={String(tl.id)}>{tl.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button data-testid="submit-user-btn" type="submit" disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Create User
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetDialog} onOpenChange={(v) => { if (!v) setResetDialog(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>New Password</Label><Input data-testid="reset-password-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} /></div>
            <Button data-testid="reset-password-btn" onClick={handleResetPassword} className="w-full bg-slate-900 hover:bg-slate-800">Reset Password</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Assignment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className={!u.is_active ? 'opacity-50' : ''}>
                    <td className="font-medium text-slate-900">{u.name}</td>
                    <td className="text-slate-600 font-mono text-xs">{u.username}</td>
                    <td>{roleBadge(u.role)}</td>
                    <td>
                      <Badge className={`text-xs ${u.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="text-slate-600 text-xs">
                      {u.assigned_precincts?.join(', ') || '-'}
                    </td>
                    <td>
                      {u.role !== 'admin' && (
                        <div className="flex gap-1">
                          <Button data-testid={`deactivate-user-${u.id}`} variant="ghost" size="sm" onClick={() => handleDeactivate(u.id)} title={u.is_active ? 'Deactivate' : 'Activate'}>
                            {u.is_active ? <Ban className="w-4 h-4 text-amber-500" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
                          </Button>
                          <Button data-testid={`reset-pw-${u.id}`} variant="ghost" size="sm" onClick={() => { setResetDialog(u.id); setNewPassword(''); }}>
                            <KeyRound className="w-4 h-4 text-blue-400" />
                          </Button>
                          <Button data-testid={`delete-user-${u.id}`} variant="ghost" size="sm" onClick={() => handleDelete(u.id)}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
